/**
 * IoT Shack Dashboards
 * Renders dashboard definitions stored in IoT Shack.
 */

import { useMemo, useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { 
  bootstrapIoTDashboards, 
  fetchFleetHealth, 
  fetchIoTDashboards, 
  fetchMessageRate,
  fetchDeviceFreshness,
  fetchTopNoisiestDevices
} from './api';
import type { IoTDashboard, DashboardWidget } from './types';
import DashboardEditor from './DashboardEditor';

export default function DashboardsPage() {
  const dashboardsQuery = useQuery({
    queryKey: ['iot', 'dashboards'],
    queryFn: fetchIoTDashboards,
  });

  const bootstrapMutation = useMutation({
    mutationFn: bootstrapIoTDashboards,
    onSuccess: () => dashboardsQuery.refetch(),
  });

  const dashboards = dashboardsQuery.data || [];
  const [selectedId, setSelectedId] = useState<string>('');
  const [isEditing, setIsEditing] = useState(false);

  const selected = useMemo<IoTDashboard | null>(() => {
    if (!dashboards.length) return null;
    const id = selectedId || dashboards[0]?.dashboardId;
    return dashboards.find((d) => d.dashboardId === id) || dashboards[0] || null;
  }, [dashboards, selectedId]);

  if (dashboardsQuery.isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (dashboardsQuery.isError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Failed to load dashboards: {String(dashboardsQuery.error)}</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Dashboards</h1>
          <p className="text-gray-600 mt-1">Custom IoT analytics dashboards (per application)</p>
        </div>

        {dashboards.length === 0 && (
          <button
            onClick={() => bootstrapMutation.mutate()}
            disabled={bootstrapMutation.isPending}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
          >
            {bootstrapMutation.isPending ? 'Creating…' : 'Create default dashboards'}
          </button>
        )}
      </div>

      {dashboards.length === 0 ? (
        <div className="bg-white rounded-lg border border-gray-200 p-12 text-center">
          <h3 className="text-lg font-medium text-gray-900 mb-2">No dashboards yet</h3>
          <p className="text-gray-600">Create defaults to get started, then we’ll iterate based on your needs.</p>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-lg border border-gray-200 p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <label className="text-sm text-gray-700">Dashboard</label>
              <select
                value={selected?.dashboardId || ''}
                onChange={(e) => {
                  setSelectedId(e.target.value);
                  setIsEditing(false); // Exit edit mode when switching dashboards
                }}
                className="border border-gray-300 rounded-md px-3 py-2 text-sm"
              >
                {dashboards.map((d) => (
                  <option key={d.dashboardId} value={d.dashboardId}>
                    {d.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-xs text-gray-500">{selected?.dashboardId}</div>
              {selected && (
                <button
                  onClick={() => setIsEditing(!isEditing)}
                  className={`px-3 py-1 rounded-md text-sm font-medium ${
                    isEditing
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {isEditing ? 'Editing...' : 'Edit'}
                </button>
              )}
            </div>
          </div>

          {selected && (
            <>
              {isEditing ? (
                <DashboardEditor dashboard={selected} onExitEdit={() => setIsEditing(false)} />
              ) : (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {selected.layout?.widgets?.map((w) => (
                    <WidgetCard key={w.id} widget={w} />
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}

function WidgetCard({ widget }: { widget: DashboardWidget }) {
  if (widget.type === 'fleet_health') return <FleetHealthWidget widget={widget} />;
  if (widget.type === 'message_rate') return <MessageRateWidget widget={widget} />;
  if (widget.type === 'device_freshness_table') return <DeviceFreshnessWidget widget={widget} />;
  if (widget.type === 'top_noisiest_devices') return <TopNoisiestDevicesWidget widget={widget} />;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900">{widget.title}</h3>
      <p className="text-sm text-gray-600 mt-2">Unsupported widget type: {widget.type}</p>
    </div>
  );
}

function FleetHealthWidget({ widget }: { widget: DashboardWidget }) {
  const minutesRaw = Number(widget.config?.offlineAfterMinutes);
  const minutes = Number.isFinite(minutesRaw) && minutesRaw > 0 ? minutesRaw : 15;

  const q = useQuery({
    queryKey: ['iot', 'analytics', 'fleet-health', minutes],
    queryFn: () => fetchFleetHealth({ offlineAfterMinutes: minutes }),
    refetchInterval: 15000,
  });

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-start justify-between">
        <h3 className="text-lg font-semibold text-gray-900">{widget.title}</h3>
        <span className="text-xs text-gray-500">offline after {minutes}m</span>
      </div>

      {q.isLoading && <p className="text-sm text-gray-500 mt-4">Loading…</p>}
      {q.isError && <p className="text-sm text-red-700 mt-4">Error: {String(q.error)}</p>}
      {q.data && (
        <div className="mt-4 space-y-4">
          <div className="flex gap-4">
            <Stat label="Online" value={q.data.counts.online} />
            <Stat label="Offline" value={q.data.counts.offline} />
            <Stat label="Unknown" value={q.data.counts.unknown} />
          </div>

          <div>
            <p className="text-sm font-medium text-gray-900 mb-2">Top offline devices</p>
            <div className="text-xs text-gray-700 space-y-1">
              {q.data.devices
                .filter((d) => d.status === 'offline')
                .slice(0, 8)
                .map((d) => (
                  <div key={d.deviceId} className="flex items-center justify-between">
                    <span className="font-mono">{d.deviceId}</span>
                    <span className="text-gray-500">{d.lastTelemetryAt ? new Date(d.lastTelemetryAt).toLocaleString() : '—'}</span>
                  </div>
                ))}
              {q.data.devices.filter((d) => d.status === 'offline').length === 0 && (
                <div className="text-gray-500">None</div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MessageRateWidget({ widget }: { widget: DashboardWidget }) {
  const windowMinutes = Number(widget.config?.windowMinutes) || 60;
  const bucketMinutes = Number(widget.config?.bucketMinutes) || 1;

  const q = useQuery({
    queryKey: ['iot', 'analytics', 'message-rate', windowMinutes, bucketMinutes],
    queryFn: () => fetchMessageRate({ windowMinutes, bucketMinutes }),
    refetchInterval: 15000,
  });

  const latest = q.data?.points?.length ? q.data.points[q.data.points.length - 1] : null;

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-start justify-between">
        <h3 className="text-lg font-semibold text-gray-900">{widget.title}</h3>
        <span className="text-xs text-gray-500">
          window {windowMinutes}m • bucket {bucketMinutes}m
        </span>
      </div>

      {q.isLoading && <p className="text-sm text-gray-500 mt-4">Loading…</p>}
      {q.isError && <p className="text-sm text-red-700 mt-4">Error: {String(q.error)}</p>}
      {q.data && (
        <div className="mt-4 space-y-3">
          <div className="flex gap-4">
            <Stat label="Latest bucket" value={latest?.count ?? 0} />
            <Stat label="Points" value={q.data.points.length} />
          </div>
          <div className="text-xs text-gray-700">
            {q.data.note && <div className="text-gray-500 mb-2">{q.data.note}</div>}
            <div className="space-y-1">
              {q.data.points.slice(-8).map((p) => (
                <div key={p.t} className="flex items-center justify-between">
                  <span className="text-gray-500">{new Date(p.t).toLocaleTimeString()}</span>
                  <span className="font-mono">{p.count}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="bg-gray-50 border border-gray-200 rounded-md px-3 py-2">
      <div className="text-xs text-gray-600">{label}</div>
      <div className="text-lg font-semibold text-gray-900">{value}</div>
    </div>
  );
}

function DeviceFreshnessWidget({ widget }: { widget: DashboardWidget }) {
  const offlineAfterMinutes = Number(widget.config?.offlineAfterMinutes) || 15;
  const limit = Number(widget.config?.limit) || 50;
  const filters = widget.filters || {};

  const q = useQuery({
    queryKey: ['iot', 'analytics', 'device-freshness', offlineAfterMinutes, limit, filters],
    queryFn: () => fetchDeviceFreshness({ 
      offlineAfterMinutes, 
      limit,
      ...filters
    }),
    refetchInterval: 15000,
  });

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-start justify-between">
        <h3 className="text-lg font-semibold text-gray-900">{widget.title}</h3>
        <span className="text-xs text-gray-500">offline after {offlineAfterMinutes}m</span>
      </div>

      {q.isLoading && <p className="text-sm text-gray-500 mt-4">Loading…</p>}
      {q.isError && <p className="text-sm text-red-700 mt-4">Error: {String(q.error)}</p>}
      {q.data && (
        <div className="mt-4">
          <div className="text-xs text-gray-600 mb-2">
            Showing {q.data.devices.length} of {limit} devices
          </div>
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {q.data.devices.map((d: any) => (
              <div key={d.deviceId} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                <div className="flex-1">
                  <div className="text-sm font-mono font-medium text-gray-900">{d.deviceId}</div>
                  <div className="text-xs text-gray-600">{d.type}</div>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`px-2 py-1 text-xs font-semibold rounded-full ${
                      d.status === 'online'
                        ? 'bg-green-100 text-green-800'
                        : d.status === 'offline'
                        ? 'bg-red-100 text-red-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {d.status}
                  </span>
                  <span className="text-xs text-gray-500">
                    {d.lastTelemetryAt ? new Date(d.lastTelemetryAt).toLocaleTimeString() : '—'}
                  </span>
                </div>
              </div>
            ))}
            {q.data.devices.length === 0 && (
              <div className="text-sm text-gray-500 text-center py-4">No devices found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function TopNoisiestDevicesWidget({ widget }: { widget: DashboardWidget }) {
  const windowMinutes = Number(widget.config?.windowMinutes) || 60;
  const limit = Number(widget.config?.limit) || 10;
  const filters = widget.filters || {};

  const q = useQuery({
    queryKey: ['iot', 'analytics', 'top-noisiest', windowMinutes, limit, filters],
    queryFn: () => fetchTopNoisiestDevices({ windowMinutes, limit, ...filters }),
    refetchInterval: 30000,
  });

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-start justify-between">
        <h3 className="text-lg font-semibold text-gray-900">{widget.title}</h3>
        <span className="text-xs text-gray-500">window {windowMinutes}m</span>
      </div>

      {q.isLoading && <p className="text-sm text-gray-500 mt-4">Loading…</p>}
      {q.isError && <p className="text-sm text-red-700 mt-4">Error: {String(q.error)}</p>}
      {q.data && (
        <div className="mt-4">
          {q.data.note && <div className="text-xs text-gray-500 mb-2">{q.data.note}</div>}
          <div className="space-y-2">
            {q.data.devices.map((d: any, idx: number) => (
              <div key={d.deviceId} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                <div className="flex items-center gap-2">
                  <span className="text-xs font-bold text-gray-500">#{idx + 1}</span>
                  <div>
                    <div className="text-sm font-mono font-medium text-gray-900">{d.deviceId}</div>
                    <div className="text-xs text-gray-600">{d.type}</div>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-blue-600">{d.messageCount}</div>
                  <div className="text-xs text-gray-500">{d.messagesPerMinute}/min</div>
                </div>
              </div>
            ))}
            {q.data.devices.length === 0 && (
              <div className="text-sm text-gray-500 text-center py-4">No data found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

